import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';

import { GET, httpOk } from 'helpers/api';
import { formatMoney } from 'helpers/helpers';
import CustomSelect from 'components/CustomSelect';

function Analytics() {
    const { t } = useTranslation();

    const units = [
        { id: 'kg', name: t('kg') },
        { id: 'box', name: t('box') },
    ];

    const periods = [
        { id: 'day', name: t('day') },
        { id: 'week', name: t('week') },
        { id: 'month', name: t('month') },
    ];

    const initFilterData = {
        unit: 'kg',
        period: 'week',
    };

    const [filterData, setFilterData] = useState(initFilterData);

    const [data, setData] = useState({
        dashboard: null,
        categoryOutput: null,
        distributorShipments: null,
        productMargin: null,
        rawConsumption: null,
        productDetailRes: null,
    });

    const getData = useCallback(async () => {
        const [
            dashboardRes,
            categoryOutputRes,
            distributorShipmentsRes,
            productMarginRes,
            rawConsumptionRes,
            productDetailRes,
        ] = await Promise.all([
            GET('/analytics/management-dashboard/', filterData),
            GET('/analytics/management-dashboard/category-output/', filterData),
            GET('/analytics/management-dashboard/distributor-shipments/', filterData),
            GET('/analytics/management-dashboard/product-margin/', filterData),
            GET('/analytics/management-dashboard/raw-consumption/', filterData),
            GET('/analytics/management-dashboard/product-detail/', filterData),
        ]);

        if (httpOk(dashboardRes)) {
            setData({
                dashboard: dashboardRes.data,
                categoryOutput: categoryOutputRes.data,
                distributorShipments: distributorShipmentsRes.data,
                productMargin: productMarginRes.data,
                rawConsumption: rawConsumptionRes.data,
                productDetailRes: productDetailRes.data,
            });
        }
    }, [filterData]);

    useEffect(() => {
        getData();
    }, [getData]);

    // 1. Расход сырья
    const rawCategories = data.rawConsumption?.series?.[0]?.points.map(p => p.date) || [];
    const rawSeries = data.rawConsumption?.series?.map(s => ({
        name: s.name,
        data: s.points.map(p => parseFloat(p.quantity))
    })) || [];
    const rawOptions = {
        chart: { type: 'line', toolbar: { show: false } },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: { categories: rawCategories },
        markers: { size: 4 },
        legend: {
            show: false
        }
    };

    // 2. Выпуск по категориям
    const categoryCategories = data.categoryOutput?.series?.[0]?.points.map(p => p.date) || [];
    const categorySeries = data.categoryOutput?.series?.map(s => ({
        name: s.name,
        data: s.points.map(p => parseFloat(p.quantity))
    })) || [];
    const categoryOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 2 } },
        dataLabels: { enabled: false },
        xaxis: { categories: categoryCategories }
    };

    // 3. Отгрузки по дистрибьюторам
    const pieSeries = data.distributorShipments?.items?.map(i => parseFloat(i.quantity)) || [];
    const pieLabels = data.distributorShipments?.items?.map(i => i.name) || [];
    const pieOptions = {
        chart: { type: 'pie' },
        labels: pieLabels,
        dataLabels: { enabled: true }
    };

    // 4. Анализ маржинальности
    const marginCategories = data.productMargin?.items?.map(i => i.name) || [];
    const marginSeries = [
        {
            name: t('revenue_currency'),
            data: data.productMargin?.items?.map(i => parseFloat(i.revenue)) || []
        },
        {
            name: t('cost_currency'),
            data: data.productMargin?.items?.map(i => parseFloat(i.cost)) || []
        }
    ];
    const marginOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, dataLabels: { position: 'top' } } },
        dataLabels: { enabled: false },
        xaxis: { categories: marginCategories }
    };

    const metrics = data.dashboard?.metrics || {};

    return (
        <>
            <div className="d-flex justify-content-between">
                <div>
                    <div className="main-content-title">
                        {t('analytics_title')}
                    </div>
                    <div className="main-content-subtitle">
                        {t('analytics_subtitle')}
                    </div>
                </div>

                <div className="d-flex gap-8">
                    <CustomSelect
                        options={units}
                        value={units.find(option => option.id === filterData?.unit) || ''}
                        onChange={(option) => setFilterData({ ...filterData, unit: option?.id })}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        isClearable={false}
                    />

                    <CustomSelect
                        options={periods}
                        value={periods.find(option => option.id === filterData?.period) || ''}
                        onChange={(option) => setFilterData({ ...filterData, period: option?.id })}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        isClearable={false}
                    />
                </div>
            </div>

            <div className="grid4 gap-16">
                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">{t('production_output')}</div>
                            <div className="value">
                                {formatMoney(metrics.production_output?.value)} {metrics.production_output?.unit}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">{t('shipments')}</div>
                            <div className="value">
                                {formatMoney(metrics.shipments?.value)} {metrics.shipments?.unit}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">{t('average_margin')}</div>
                            <div className="value">
                                {metrics.average_margin?.value ? `${formatMoney(metrics.average_margin.value)} ${metrics.average_margin.unit}` : '0 %'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">{t('turnover')}</div>
                            <div className="value">
                                {formatMoney(metrics.turnover_days?.value || 0)} {metrics.turnover_days?.unit}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid2 gap-24 mb-24 mt-24">
                <div className="card">
                    <div className="fz16 text-third mb-16">{t('raw_consumption_chart')}</div>
                    {data.rawConsumption && (
                        <Chart
                            options={rawOptions}
                            series={rawSeries}
                            type="line"
                            height={400}
                            className="w-100"
                        />
                    )}
                </div>

                <div className="card">
                    <div className="fz16 text-third mb-16">{t('category_output_chart')}</div>
                    {data.categoryOutput && (
                        <Chart
                            options={categoryOptions}
                            series={categorySeries}
                            type="bar"
                            height={400}
                            className="w-100"
                        />
                    )}
                </div>
            </div>

            <div className="grid2 gap-24 mb-24">
                <div className="card">
                    <div className="fz16 text-third mb-16">{t('distributor_shipments_chart')}</div>
                    {data.distributorShipments && (
                        <Chart
                            options={pieOptions}
                            series={pieSeries}
                            type="pie"
                            height={400}
                            className="w-100"
                        />
                    )}
                </div>

                <div className="card">
                    <div className="fz16 text-third mb-16">{t('margin_analysis_chart')}</div>
                    {data.productMargin && (
                        <Chart
                            options={marginOptions}
                            series={marginSeries}
                            type="bar"
                            height={400}
                            // height={Math.max(320, marginCategories.length * 30)}
                            className="w-100"
                        />
                    )}
                </div>
            </div>

            <div className="card">
                <div className="fz16 text-third mb-16">
                    {t('detailed_product_analysis')}
                </div>

                <table className="table">
                    <thead>
                        <tr>
                            <th>{t('product')}</th>
                            <th>{t('revenue')}</th>
                            <th>{t('cost')}</th>
                            <th>{t('profit')}</th>
                            <th>{t('margin_percent')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.productDetailRes?.items?.map((item, index) => (
                            <tr key={index}>
                                <td>{item.name}</td>
                                <td>{formatMoney(item.revenue)}</td>
                                <td>{formatMoney(item.cost)}</td>
                                <td>{formatMoney(item.margin)}</td>
                                <td>{formatMoney(item.margin_percent)} %</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default Analytics;